import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContractBasicsForm = ({ formData, onChange, errors }) => {
  const contractTypes = [
    { value: 'service-agreement', label: 'Service Agreement' },
    { value: 'master-service-agreement', label: 'Master Service Agreement' },
    { value: 'nda', label: 'Non-Disclosure Agreement' },
    { value: 'employment', label: 'Employment Contract' },
    { value: 'vendor-agreement', label: 'Vendor Agreement' },
    { value: 'licensing', label: 'Licensing Agreement' },
    { value: 'partnership', label: 'Partnership Agreement' },
    { value: 'consulting', label: 'Consulting Agreement' }
  ];

  const templates = [
    { value: '', label: 'Start from scratch' },
    { value: 'standard-service', label: 'Standard Service Agreement Template' },
    { value: 'master-service', label: 'Master Service Agreement Template' },
    { value: 'nda-mutual', label: 'Mutual NDA Template' },
    { value: 'consulting-basic', label: 'Basic Consulting Agreement Template' }
  ];

  const handleInputChange = (field, value) => {
    onChange(field, value);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <span className="text-primary font-semibold text-sm">1</span>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Contract Basics</h3>
          <p className="text-sm text-muted-foreground">Define the fundamental contract information</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Template Selection */}
        <Select
          label="Start with Template"
          description="Choose a template to pre-populate common fields"
          options={templates}
          value={formData?.template || ''}
          onChange={(value) => handleInputChange('template', value)}
          placeholder="Select a template (optional)"
        />

        {/* Contract Title */}
        <Input
          label="Contract Title"
          type="text"
          placeholder="Enter contract title (e.g., Software Development Services Agreement)"
          value={formData?.title || ''}
          onChange={(e) => handleInputChange('title', e?.target?.value)}
          error={errors?.title}
          required
          description="A clear, descriptive title for the contract"
        />

        {/* Contract Type */}
        <Select
          label="Contract Type"
          description="Select the category that best describes this contract"
          options={contractTypes}
          value={formData?.type || ''}
          onChange={(value) => handleInputChange('type', value)}
          error={errors?.type}
          required
          placeholder="Choose contract type"
        />

        {/* Contract ID */}
        <Input
          label="Contract ID"
          type="text"
          placeholder="Auto-generated (e.g., CT-2024-001) or enter custom ID"
          value={formData?.contractId || ''}
          onChange={(e) => handleInputChange('contractId', e?.target?.value)}
          description="Unique identifier for this contract. Leave blank for auto-generation."
        />

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Contract Description
            <span className="text-error ml-1">*</span>
          </label>
          <textarea
            placeholder="Provide a detailed description of the contract purpose, scope, and key objectives..."
            value={formData?.description || ''}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            className="w-full min-h-[120px] px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
          />
          {errors?.description && (
            <p className="text-sm text-error">{errors?.description}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Describe the contract's purpose, scope, and key deliverables. This will help with contract organization and search.
          </p>
        </div>

        {/* Priority Level */}
        <Select
          label="Priority Level"
          description="Set the priority level for this contract"
          options={[
            { value: 'low', label: 'Low Priority' },
            { value: 'medium', label: 'Medium Priority' },
            { value: 'high', label: 'High Priority' },
            { value: 'critical', label: 'Critical Priority' }
          ]}
          value={formData?.priority || 'medium'}
          onChange={(value) => handleInputChange('priority', value)}
        />

        {/* Tags */}
        <Input
          label="Tags"
          type="text"
          placeholder="Enter tags separated by commas (e.g., software, development, recurring)"
          value={formData?.tags || ''}
          onChange={(e) => handleInputChange('tags', e?.target?.value)}
          description="Add tags to help categorize and search for this contract"
        />
      </div>
    </div>
  );
};

export default ContractBasicsForm;